import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, Lock } from '../../components/icons';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { StageContent } from '../../components/pathway/StageContent';
import {
  fetchPathwayForRespondent,
  upsertStageProgress,
  upsertTaskResponse,
  markPathwayComplete,
} from '../../lib/pathways';
import type { PathwayDetail, PathwayProgress, PathwayTaskResponse } from '../../types/pathway';

export function PathwayViewTR() {
  const navigate = useNavigate();
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const { user } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [pathway, setPathway] = useState<PathwayDetail | null>(null);
  const [progress, setProgress] = useState<PathwayProgress[]>([]);
  const [taskResponses, setTaskResponses] = useState<PathwayTaskResponse[]>([]);
  const [activeStageIdx, setActiveStageIdx] = useState(0);

  useEffect(() => {
    if (!user) { navigate('/tr/login'); return; }
    if (!assignmentId) { navigate('/tr/dashboard'); return; }
    loadData();
  }, [user, navigate, assignmentId]);

  const loadData = async () => {
    if (!user?.id || !assignmentId) return;
    try {
      const data = await fetchPathwayForRespondent(assignmentId);
      if (!data) { navigate('/tr/dashboard'); return; }

      setPathway(data.pathway);
      setProgress(data.progress);
      setTaskResponses(data.taskResponses);

      const firstIncomplete = data.pathway.stages.findIndex(stage => {
        const p = data.progress.find(pp => pp.stageId === stage.id);
        return !p || p.status !== 'completed';
      });
      setActiveStageIdx(firstIncomplete >= 0 ? firstIncomplete : 0);
    } catch (err) {
      console.error('Error loading pathway:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const getStageProgress = (stageId: string) => progress.find(p => p.stageId === stageId);

  const isStageUnlocked = (stageIdx: number) => {
    if (stageIdx === 0) return true;
    const prevStage = pathway?.stages[stageIdx - 1];
    if (!prevStage) return false;
    return getStageProgress(prevStage.id)?.status === 'completed';
  };

  const handleStartStage = async () => {
    if (!assignmentId || !pathway) return;
    const stage = pathway.stages[activeStageIdx];
    try {
      const progressId = await upsertStageProgress(assignmentId, stage.id, 'in_progress');
      setProgress(prev => {
        const existing = prev.findIndex(p => p.stageId === stage.id);
        const newEntry: PathwayProgress = { id: progressId, assignmentId, stageId: stage.id, status: 'in_progress', startedAt: new Date().toISOString(), completedAt: null };
        if (existing >= 0) { const updated = [...prev]; updated[existing] = newEntry; return updated; }
        return [...prev, newEntry];
      });
    } catch (err) { console.error(err); }
  };

  const handleCompleteStage = async () => {
    if (!assignmentId || !pathway) return;
    const stage = pathway.stages[activeStageIdx];
    try {
      const progressId = await upsertStageProgress(assignmentId, stage.id, 'completed');
      setProgress(prev => {
        const existing = prev.findIndex(p => p.stageId === stage.id);
        const newEntry: PathwayProgress = { id: progressId, assignmentId, stageId: stage.id, status: 'completed', startedAt: prev.find(p => p.stageId === stage.id)?.startedAt || new Date().toISOString(), completedAt: new Date().toISOString() };
        if (existing >= 0) { const updated = [...prev]; updated[existing] = newEntry; return updated; }
        return [...prev, newEntry];
      });

      const allCompleted = pathway.stages.every((s, i) => {
        if (i === activeStageIdx) return true;
        return progress.find(pp => pp.stageId === s.id)?.status === 'completed';
      });

      if (allCompleted) { await markPathwayComplete(assignmentId); }
      else if (activeStageIdx < pathway.stages.length - 1) { setActiveStageIdx(activeStageIdx + 1); }
    } catch (err) { console.error(err); }
  };

  const handleSaveResponse = async (taskId: string, response: string) => {
    if (!assignmentId || !pathway) return;
    const stage = pathway.stages[activeStageIdx];
    const stageProgress = getStageProgress(stage.id);

    let progressId = stageProgress?.id;
    if (!progressId) {
      progressId = await upsertStageProgress(assignmentId, stage.id, 'in_progress');
      setProgress(prev => [...prev, { id: progressId!, assignmentId, stageId: stage.id, status: 'in_progress' as const, startedAt: new Date().toISOString(), completedAt: null }]);
    }

    try {
      await upsertTaskResponse(progressId, taskId, response);
      setTaskResponses(prev => {
        const existing = prev.findIndex(tr => tr.progressId === progressId && tr.taskId === taskId);
        const entry: PathwayTaskResponse = { id: existing >= 0 ? prev[existing].id : crypto.randomUUID(), progressId: progressId!, taskId, response, createdAt: existing >= 0 ? prev[existing].createdAt : new Date().toISOString(), updatedAt: new Date().toISOString() };
        if (existing >= 0) { const updated = [...prev]; updated[existing] = entry; return updated; }
        return [...prev, entry];
      });
    } catch (err) { console.error(err); }
  };

  if (isLoading) return <LoadingSpinner message="Yolculuk yukleniyor..." />;
  if (!pathway) return null;

  const activeStage = pathway.stages[activeStageIdx];
  const completedCount = pathway.stages.filter(s => getStageProgress(s.id)?.status === 'completed').length;
  const overallProgress = pathway.stages.length > 0 ? Math.round((completedCount / pathway.stages.length) * 100) : 0;

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <Link to="/tr/dashboard" className="p-2 hover:bg-cream-200 rounded-xl transition-colors">
                <ArrowLeft className="w-5 h-5 text-chocolate-600" />
              </Link>
              <div>
                <h1 className="freud-heading-base text-chocolate-900">{pathway.name}</h1>
                <p className="text-xs text-orange-50">
                  {completedCount} / {pathway.stages.length} asama tamamlandi
                </p>
              </div>
            </div>
            <span className="text-sm font-medium text-chocolate-600">%{overallProgress}</span>
          </div>
          <div className="h-1.5 bg-cream-200 rounded-full overflow-hidden">
            <div className="h-full bg-orange-50 rounded-full transition-all duration-500" style={{ width: `${overallProgress}%` }} />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-1">
              <p className="text-xs font-semibold text-chocolate-600 uppercase tracking-wider mb-3">Asamalar</p>
              {pathway.stages.map((stage, i) => {
                const stageProgress = getStageProgress(stage.id);
                const isComplete = stageProgress?.status === 'completed';
                const isActive = i === activeStageIdx;
                const unlocked = isStageUnlocked(i);

                return (
                  <button
                    key={stage.id}
                    onClick={() => unlocked && setActiveStageIdx(i)}
                    disabled={!unlocked}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all text-left ${isActive ? 'bg-orange-10 border border-orange-40' : unlocked ? 'hover:bg-cream-50' : 'opacity-50 cursor-not-allowed'}`}
                  >
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-medium ${isComplete ? 'bg-green-100 text-green-700' : isActive ? 'bg-orange-50 text-white' : unlocked ? 'bg-cream-200 text-chocolate-500' : 'bg-cream-100 text-chocolate-300'}`}>
                      {isComplete ? <Check className="w-3.5 h-3.5" /> : !unlocked ? <Lock className="w-3 h-3" /> : i + 1}
                    </div>
                    <div className="min-w-0">
                      <p className={`text-xs font-medium truncate ${isActive ? 'text-orange-70' : 'text-chocolate-700'}`}>
                        {stage.title || `Asama ${i + 1}`}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-3">
            {activeStage && (
              <StageContent
                stage={activeStage}
                progress={getStageProgress(activeStage.id)}
                taskResponses={taskResponses.filter(tr => {
                  const stageP = getStageProgress(activeStage.id);
                  return stageP && tr.progressId === stageP.id;
                })}
                isLocked={!isStageUnlocked(activeStageIdx)}
                onStartStage={handleStartStage}
                onCompleteStage={handleCompleteStage}
                onSaveResponse={handleSaveResponse}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
